import React, { useEffect, useState } from 'react';
import { publicApiCall } from '../services/api';

interface VersionResponse {
  service: string;
  frontendVersion: string;
  backendVersion: string;
  deployedAt: string;
  latestCommit: string;
  mismatch?: true;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ok'; data: VersionResponse }
  | { status: 'error'; message: string };

const APP_VERSION: string = process.env.REACT_APP_VERSION ?? '?.?.?';

export default function VersionPage() {
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    publicApiCall('/version')
      .then((data) => {
        if (!cancelled) setState({ status: 'ok', data: data as VersionResponse });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Unable to reach the version endpoint.';
          setState({ status: 'error', message });
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-2xl mx-auto mt-12 px-4">
      <h1 className="text-2xl font-semibold text-gray-800 mb-6">Application Version</h1>

      {/* Frontend version is always available — baked in at build time */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-100 mb-6">
        <Row label="Frontend version" value={`v${APP_VERSION}`} />
      </div>

      {/* Backend version info fetched from GET /version */}
      {state.status === 'loading' && (
        <div className="flex items-center gap-3 text-gray-500 text-sm">
          <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 inline-block" />
          Fetching backend version…
        </div>
      )}

      {state.status === 'error' && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span className="font-medium">Could not load backend version info.</span>{' '}
          {state.message}
        </div>
      )}

      {state.status === 'ok' && (
        <>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-100">
            <Row label="Service"          value={state.data.service} />
            <Row label="Backend version"  value={`v${state.data.backendVersion}`} />
            <Row label="Frontend version" value={`v${state.data.frontendVersion}`} />
            <Row label="Deployed at"      value={new Date(state.data.deployedAt).toLocaleString()} />
            <Row label="Latest commit"    value={state.data.latestCommit} />
            {state.data.mismatch && (
              <div className="px-4 py-3 flex items-center gap-2 text-sm text-amber-700 bg-amber-50">
                <span className="font-medium">⚠ Version mismatch</span> — frontend and backend
                were built from different releases.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-4 py-3 flex justify-between items-center text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-mono text-gray-800">{value}</span>
    </div>
  );
}
